// Tokenization Console — Bank Admin tab (Prompt 12).
// Five sections: Issuance Overview / Holder Ledger / Mint-Burn Operations / Product Config / Smart Contracts.

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Coins, RefreshCw, Search, Plus, Loader2, CheckCircle2, AlertTriangle,
  XCircle, ShieldCheck, Pause, Flame, Download, FileText, X, Lock,
  TrendingUp, Activity, Building2, ChevronRight,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ReferenceDot,
} from "recharts";
import { useBranding } from "../../BrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2/admin/tokenization`;

const EMERALD = "#10b981";       // canonical Tokenized Deposit color
const ROSE = "#dc2626";

// ── Helpers ──

const fmtUSD = (n, dec = 2) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
const fmtUSDCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return fmtUSD(v);
};
const fmtRel = (iso) => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};
const truncAddr = (a) => a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—";

const STATUS_PILL = {
  active: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700",
  winding_down: "bg-slate-200 text-slate-700",
  deprecated: "bg-slate-200 text-slate-500",
  scheduled: "bg-sky-100 text-sky-700",
  verified: "bg-emerald-100 text-emerald-700",
};

const StatusPill = ({ status }) => (
  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${STATUS_PILL[status] || "bg-slate-100 text-slate-700"}`}>
    {status?.replace(/_/g, " ") || "—"}
  </span>
);

const Toast = ({ msg, kind = "success", onClose }) => {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 2800);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  const bg = kind === "success" ? "bg-emerald-600" : kind === "error" ? "bg-rose-600" : "bg-slate-900";
  return (
    <div className={`fixed bottom-6 right-6 ${bg} text-white px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 z-50 text-sm`} data-testid="tk-toast">
      <CheckCircle2 className="w-4 h-4" />{msg}
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 1 — Token Issuance Overview
// ─────────────────────────────────────────────────

const OverviewSection = ({ data, onIssueNew }) => {
  if (!data) {
    return <div className="bg-white rounded-xl border border-slate-200 p-5 h-64 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  const p = data.product;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="tk-overview-section">
      {/* Header bar */}
      <div className="bg-gradient-to-r from-emerald-50 to-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
            <Coins className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold text-slate-900 tracking-tight" data-testid="tk-product-symbol">{p.symbol}</h3>
              <StatusPill status={p.status} />
            </div>
            <p className="text-xs text-slate-500">{p.display_name}</p>
          </div>
        </div>
        <button onClick={onIssueNew} data-testid="tk-issue-new-btn"
          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Issue New Token
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5">
        <div data-testid="tk-kpi-total-supply">
          <p className="text-[11px] uppercase font-semibold text-slate-500 tracking-wide">Total Supply</p>
          <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{fmtUSDCompact(data.kpis.total_supply_usd)}</p>
          <p className="text-[11px] text-slate-500">tokens minted (lifetime)</p>
        </div>
        <div data-testid="tk-kpi-circulating">
          <p className="text-[11px] uppercase font-semibold text-slate-500 tracking-wide">Circulating</p>
          <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{fmtUSDCompact(data.kpis.circulating_usd)}</p>
          <p className="text-[11px] text-slate-500">held by members</p>
        </div>
        <div data-testid="tk-kpi-treasury">
          <p className="text-[11px] uppercase font-semibold text-slate-500 tracking-wide">Bank Treasury</p>
          <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{fmtUSDCompact(data.kpis.treasury_usd)}</p>
          <p className="text-[11px] text-slate-500">held by bank, not circulating</p>
        </div>
        <div data-testid="tk-kpi-net-30d">
          <p className="text-[11px] uppercase font-semibold text-slate-500 tracking-wide">Net Issuance (30d)</p>
          <p className={`text-2xl font-bold mt-1 tabular-nums ${data.kpis.net_issuance_30d_usd >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {data.kpis.net_issuance_30d_usd >= 0 ? "+" : ""}{fmtUSDCompact(data.kpis.net_issuance_30d_usd)}
          </p>
          <p className="text-[11px] text-slate-500">mints − burns</p>
        </div>
      </div>

      {/* Supply chart with event markers */}
      <div className="px-5 pb-3">
        <p className="text-xs font-bold uppercase text-slate-500 mb-2">Supply Trend (30 days)</p>
        <div className="h-52 bg-slate-50 rounded-lg p-3" data-testid="tk-supply-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.supply_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={fmtUSDCompact} domain={["auto", "auto"]} />
              <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Line type="monotone" dataKey="supply_usd" stroke={EMERALD} strokeWidth={2} dot={false} />
              {data.event_markers.map((m) => (
                <ReferenceDot key={m.id} x={m.date} y={data.supply_series.find((s) => s.date === m.date)?.supply_usd || null}
                  r={4} fill={m.kind === "mint" ? EMERALD : ROSE} stroke="white" strokeWidth={1.5} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-slate-500 mt-1.5">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Mint</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-600" />Burn</span>
        </div>
      </div>

      {/* Status row */}
      <div className="bg-slate-50 border-t border-slate-200 px-5 py-2.5 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-slate-600">
        <span><span className="font-semibold">Smart Contract:</span> <code className="bg-slate-200 px-1.5 py-0.5 rounded font-mono">{truncAddr(p.smart_contract_address)}</code></span>
        <span><span className="font-semibold">Chain:</span> {p.chain}</span>
        <span><span className="font-semibold">Last Audit:</span> {p.last_audit_date}</span>
        <span><span className="font-semibold">Reserve Ratio:</span> <span className="text-emerald-700 font-bold">{data.kpis.reserve_ratio.toFixed(4)}x</span></span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 2 — Holder Ledger
// ─────────────────────────────────────────────────

const HolderDetailModal = ({ memberId, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!memberId) return;
    setData(null);
    axios.get(`${API}/holders/${memberId}`).then((r) => setData(r.data));
  }, [memberId]);
  if (!memberId) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="tk-holder-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {!data ? (
          <div className="h-48 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{data.holder.display_name}</h3>
                <p className="text-xs text-slate-500 font-mono">Member ID: {data.holder.member_id}</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="tk-holder-modal-close">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] uppercase font-semibold text-slate-600">Current Balance</p>
                  <p className="text-xl font-bold text-slate-900 font-mono">{fmtUSD(data.holder.balance_usd)}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-[10px] uppercase font-semibold text-emerald-700">Yield YTD</p>
                  <p className="text-xl font-bold text-emerald-900 font-mono">{fmtUSD(data.holder.yield_ytd_usd)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] uppercase font-semibold text-slate-600">Avg Daily (30d)</p>
                  <p className="text-base font-bold text-slate-900 font-mono">{fmtUSD(data.holder.avg_daily_balance_30d)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] uppercase font-semibold text-slate-600">KYC</p>
                  <p className="text-base font-bold text-slate-900">{data.holder.kyc_tier} · <span className="text-emerald-600">{data.holder.kyc_status}</span></p>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Mint / Burn — Last 30 Days</h4>
                {data.events_30d.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4">No recent activity.</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="text-left px-3 py-2">Type</th>
                          <th className="text-right px-3 py-2">Amount</th>
                          <th className="text-left px-3 py-2">Note</th>
                          <th className="text-left px-3 py-2">When</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {data.events_30d.map((e) => (
                          <tr key={e.id}>
                            <td className="px-3 py-2"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${e.kind === "mint" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{e.kind}</span></td>
                            <td className="px-3 py-2 text-right font-mono">{fmtUSD(e.amount)}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{e.note}</td>
                            <td className="px-3 py-2 text-xs text-slate-500">{fmtRel(e.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const HolderLedgerSection = ({ toast }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("balance_usd");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/holders`, { params: { q } });
    setData(r.data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const arr = [...data.holders];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [data, sortKey, sortDir]);

  const sortHeader = (key, label, align = "left") => (
    <th onClick={() => { setSortKey(key); setSortDir(d => sortKey === key && d === "asc" ? "desc" : "asc"); }}
        className={`text-${align} px-3 py-2.5 cursor-pointer hover:text-slate-900`}>
      {label}{sortKey === key && (sortDir === "asc" ? " ↑" : " ↓")}
    </th>
  );

  const exportCsv = () => {
    if (!data) return;
    const header = "member_id,display_name,balance_usd,pct_of_circulating,joined_vault_at,last_activity\n";
    const body = sortedRows.map(r => `${r.member_id},"${r.display_name}",${r.balance_usd},${r.pct_of_circulating},${r.joined_vault_at},${r.last_activity}`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `td-seacoast-holders-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast("Holder snapshot exported");
  };

  if (!data) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="tk-holders-section">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900">Token Holders</h3>
        <p className="text-xs text-slate-500">Live registry of all member balances</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-slate-50 rounded-lg p-3" data-testid="tk-holders-kpi-total">
          <p className="text-[10px] uppercase font-semibold text-slate-600">Total Holders</p>
          <p className="text-xl font-bold text-slate-900">{data.kpis.total_holders}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3" data-testid="tk-holders-kpi-avg">
          <p className="text-[10px] uppercase font-semibold text-slate-600">Average Holding</p>
          <p className="text-xl font-bold text-slate-900 font-mono">{fmtUSD(data.kpis.average_holding_usd)}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3" data-testid="tk-holders-kpi-largest">
          <p className="text-[10px] uppercase font-semibold text-slate-600">Largest (anonymized)</p>
          <p className="text-xl font-bold text-slate-900 font-mono">{fmtUSD(data.kpis.largest_holding_usd)}</p>
          <p className="text-[10px] text-slate-500 font-mono">{data.kpis.largest_holder_member_id}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 max-w-md bg-slate-50 rounded-md px-3 py-1.5 border border-slate-200">
          <Search className="w-4 h-4 text-slate-400" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or Member ID…"
            className="flex-1 bg-transparent text-sm outline-none"
            data-testid="tk-holders-search" />
        </div>
        <button onClick={exportCsv} data-testid="tk-holders-export"
          className="px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> Export Snapshot to CSV
        </button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="tk-holders-table">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              {sortHeader("member_id", "Member ID")}
              {sortHeader("display_name", "Display Name")}
              {sortHeader("balance_usd", "Balance", "right")}
              {sortHeader("pct_of_circulating", "% of Circulating", "right")}
              {sortHeader("joined_vault_at", "Joined Vault")}
              {sortHeader("last_activity", "Last Activity")}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedRows.map((h) => (
              <tr key={h.member_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(h.member_id)} data-testid={`tk-holder-row-${h.member_id}`}>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{h.member_id}</td>
                <td className="px-3 py-2.5 text-slate-900">{h.display_name}</td>
                <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-900">{fmtUSD(h.balance_usd)}</td>
                <td className="px-3 py-2.5 text-right text-xs text-slate-600">{h.pct_of_circulating.toFixed(2)}%</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{new Date(h.joined_vault_at).toLocaleDateString()}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{fmtRel(h.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedRows.length === 0 && <div className="text-center py-8 text-sm text-slate-400">No holders match.</div>}
      </div>

      <HolderDetailModal memberId={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 3 — Mint / Burn Operations Panel
// ─────────────────────────────────────────────────

const MintBurnSection = ({ holders, toast, onChanged }) => {
  const [mintForm, setMintForm] = useState({
    member_id: "", amount: "", reason: "Vault Deposit", authorization: "standard",
  });
  const [burnForm, setBurnForm] = useState({
    member_id: "", amount: "", reason: "Vault Withdrawal", compliance_event_id: "",
  });
  const [reserveCheck, setReserveCheck] = useState(null);
  const [submittingMint, setSubmittingMint] = useState(false);
  const [submittingBurn, setSubmittingBurn] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(async () => {
    const r = await axios.get(`${API}/operations/recent`);
    setRecent(r.data.operations);
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // Reserve preflight when amount changes
  useEffect(() => {
    const amt = Number(mintForm.amount) || 0;
    if (amt <= 0) { setReserveCheck(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/reserve-check`, { params: { delta: amt } }).then((r) => setReserveCheck(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [mintForm.amount]);

  const burnHolder = useMemo(
    () => holders?.find(h => h.member_id === burnForm.member_id),
    [holders, burnForm.member_id],
  );
  const burnNewBalance = burnHolder ? burnHolder.balance_usd - (Number(burnForm.amount) || 0) : null;
  const burnApproved = burnHolder && burnNewBalance >= 0 && Number(burnForm.amount) > 0;

  const submitMint = async () => {
    if (!mintForm.member_id || !mintForm.amount) {
      toast("Recipient and amount are required", "error"); return;
    }
    setSubmittingMint(true);
    try {
      await axios.post(`${API}/mint`, {
        member_id: mintForm.member_id,
        amount: Number(mintForm.amount),
        reason: mintForm.reason,
        authorization: mintForm.authorization,
      });
      toast(`Minted ${fmtUSD(mintForm.amount)} → ${mintForm.member_id}`);
      setMintForm({ ...mintForm, amount: "" });
      await loadRecent();
      onChanged?.();
    } catch (e) {
      toast(e.response?.data?.detail || "Mint failed", "error");
    } finally {
      setSubmittingMint(false);
    }
  };

  const submitBurn = async () => {
    if (!burnForm.member_id || !burnForm.amount) {
      toast("Member and amount are required", "error"); return;
    }
    if (burnForm.reason === "Compliance Burn" && !burnForm.compliance_event_id) {
      toast("Compliance Event ID required for Compliance Burn", "error"); return;
    }
    setSubmittingBurn(true);
    try {
      await axios.post(`${API}/burn`, {
        ...burnForm,
        amount: Number(burnForm.amount),
      });
      toast(`Burned ${fmtUSD(burnForm.amount)} ← ${burnForm.member_id}`);
      setBurnForm({ ...burnForm, amount: "", compliance_event_id: "" });
      await loadRecent();
      onChanged?.();
    } catch (e) {
      toast(e.response?.data?.detail || "Burn failed", "error");
    } finally {
      setSubmittingBurn(false);
    }
  };

  const ReserveBadge = () => {
    if (!reserveCheck) return null;
    const ok = reserveCheck.approved;
    return (
      <div className={`mt-2 rounded-md p-2.5 text-xs border ${ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
           data-testid="tk-reserve-check">
        <p className="font-bold">
          {ok ? "✓ APPROVED" : "✗ INSUFFICIENT RESERVES"}
        </p>
        <p>Current ratio: {reserveCheck.current_ratio.toFixed(4)}x → after mint: <span className="font-bold">{reserveCheck.new_ratio.toFixed(4)}x</span> (floor {reserveCheck.floor.toFixed(2)}x)</p>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="tk-operations-section">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900">Token Operations</h3>
        <p className="text-xs text-slate-500">Authorized issuance and redemption controls — every action is logged to admin_audit_log</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* MINT */}
        <div className="border border-emerald-200 rounded-xl p-4 bg-emerald-50/30" data-testid="tk-mint-form">
          <h4 className="text-sm font-bold text-emerald-800 flex items-center gap-2 mb-3">
            <Plus className="w-4 h-4" /> Authorized Mint
          </h4>
          <div className="space-y-2.5">
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Recipient</label>
              <select value={mintForm.member_id} onChange={(e) => setMintForm({...mintForm, member_id: e.target.value})}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" data-testid="tk-mint-recipient">
                <option value="">Select member…</option>
                {(holders || []).map(h => (
                  <option key={h.member_id} value={h.member_id} label={`${h.member_id} · ${h.display_name}`} />
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-600">Amount (USD)</label>
                <input type="number" value={mintForm.amount} onChange={(e) => setMintForm({...mintForm, amount: e.target.value})}
                  placeholder="10000"
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono" data-testid="tk-mint-amount" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-600">Reason</label>
                <select value={mintForm.reason} onChange={(e) => setMintForm({...mintForm, reason: e.target.value})}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" data-testid="tk-mint-reason">
                  <option>Vault Deposit</option>
                  <option>Bank-Initiated Issuance</option>
                  <option>Reconciliation Adjustment</option>
                  <option>Promotion / Bonus</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Authorization</label>
              <div className="mt-1 flex gap-3 text-xs">
                <label className="inline-flex items-center gap-1.5">
                  <input type="radio" checked={mintForm.authorization === "standard"} onChange={() => setMintForm({...mintForm, authorization: "standard"})} data-testid="tk-mint-auth-std" />
                  Standard
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input type="radio" checked={mintForm.authorization === "multi_sig"} onChange={() => setMintForm({...mintForm, authorization: "multi_sig"})} data-testid="tk-mint-auth-multi" />
                  Multi-Sig (required &gt; $50K)
                </label>
              </div>
            </div>
            <ReserveBadge />
            <button onClick={submitMint}
              disabled={submittingMint || !reserveCheck?.approved || !mintForm.member_id}
              data-testid="tk-mint-submit"
              className="w-full mt-1 px-4 py-2.5 text-sm font-bold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5">
              {submittingMint ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Authorize Mint
            </button>
          </div>
        </div>

        {/* BURN */}
        <div className="border border-rose-200 rounded-xl p-4 bg-rose-50/20" data-testid="tk-burn-form">
          <h4 className="text-sm font-bold text-rose-800 flex items-center gap-2 mb-3">
            <Flame className="w-4 h-4" /> Authorized Burn
          </h4>
          <div className="space-y-2.5">
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Member to debit</label>
              <select value={burnForm.member_id} onChange={(e) => setBurnForm({...burnForm, member_id: e.target.value})}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" data-testid="tk-burn-member">
                <option value="">Select member…</option>
                {(holders || []).map(h => (
                  <option key={h.member_id} value={h.member_id} label={`${h.member_id} · ${h.display_name} (${fmtUSDCompact(h.balance_usd)})`} />
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-600">Amount (USD)</label>
                <input type="number" value={burnForm.amount} onChange={(e) => setBurnForm({...burnForm, amount: e.target.value})}
                  placeholder="5000"
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono" data-testid="tk-burn-amount" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-600">Reason</label>
                <select value={burnForm.reason} onChange={(e) => setBurnForm({...burnForm, reason: e.target.value})}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" data-testid="tk-burn-reason">
                  <option>Vault Withdrawal</option>
                  <option>Compliance Burn</option>
                  <option>Smart Contract Upgrade</option>
                  <option>Member Account Closure</option>
                  <option>Suspicious Activity Hold</option>
                </select>
              </div>
            </div>
            {burnForm.reason === "Compliance Burn" && (
              <div data-testid="tk-burn-compliance-id">
                <label className="text-[11px] uppercase font-semibold text-slate-600">Compliance Event ID (required)</label>
                <input type="text" value={burnForm.compliance_event_id}
                  onChange={(e) => setBurnForm({...burnForm, compliance_event_id: e.target.value})}
                  placeholder="CE-2026-XXXX"
                  className="mt-1 w-full px-3 py-2 border border-rose-300 rounded-md text-sm font-mono" />
              </div>
            )}
            {burnHolder && Number(burnForm.amount) > 0 && (
              <div className={`rounded-md p-2.5 text-xs border ${burnApproved ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
                   data-testid="tk-burn-balance-check">
                <p className="font-bold">{burnApproved ? "✓ APPROVED" : "✗ INSUFFICIENT BALANCE"}</p>
                <p>Member balance: {fmtUSD(burnHolder.balance_usd)} → after burn: <span className="font-bold">{fmtUSD(burnNewBalance)}</span></p>
              </div>
            )}
            <button onClick={submitBurn}
              disabled={submittingBurn || !burnApproved || (burnForm.reason === "Compliance Burn" && !burnForm.compliance_event_id)}
              data-testid="tk-burn-submit"
              className="w-full mt-1 px-4 py-2.5 text-sm font-bold rounded-md bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5">
              {submittingBurn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />}
              Authorize Burn
            </button>
          </div>
        </div>
      </div>

      {/* Recent Operations */}
      <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Recent Operations</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="tk-ops-recent">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Member</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Reason</th>
              <th className="text-left px-3 py-2">Operator</th>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Reference</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {recent.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-6 text-slate-400">No recent operations.</td></tr>
            ) : recent.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50">
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${e.kind === "mint" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{e.kind}</span>
                </td>
                <td className="px-3 py-2.5 text-slate-700">{e.counterparty}</td>
                <td className="px-3 py-2.5 text-right font-mono font-semibold text-slate-900">{fmtUSDCompact(e.amount)}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{e.note}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500 font-mono">{e.operator}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{fmtRel(e.timestamp)}</td>
                <td className="px-3 py-2.5 text-xs font-mono text-slate-500">{e.tx_reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 4 — Token Product Configuration
// ─────────────────────────────────────────────────

const ProductConfigSection = ({ toast }) => {
  const brand = useBranding();
  const [product, setProduct] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/products`);
    if (r.data.products.length) {
      setProduct(r.data.products[0]);
      setDraft({});
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!product) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const eff = (k) => draft[k] !== undefined ? draft[k] : product[k];
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/products/${product.id}/config`, draft);
      toast("Product configuration saved");
      setDraft({});
      await load();
    } catch (e) {
      toast(e.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const FieldText = ({ k, label, type = "text", placeholder, hint, suffix }) => (
    <div>
      <label className="text-[11px] uppercase font-semibold text-slate-600">{label}</label>
      <div className="relative mt-1">
        <input type={type} value={eff(k) ?? ""}
          onChange={(e) => setDraft(d => ({...d, [k]: type === "number" ? Number(e.target.value) : e.target.value}))}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border border-slate-200 rounded-md text-sm ${type === "number" ? "font-mono" : ""}`} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>}
      </div>
      {hint && <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );

  const FieldSelect = ({ k, label, options, hint }) => (
    <div>
      <label className="text-[11px] uppercase font-semibold text-slate-600">{label}</label>
      <select value={eff(k) || ""} onChange={(e) => setDraft(d => ({...d, [k]: e.target.value}))}
        className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {hint && <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="tk-config-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Token Product Settings</h3>
          <p className="text-xs text-slate-500">All edits write to bank_policy and admin_audit_log</p>
        </div>
        {dirty && (
          <button onClick={save} disabled={saving} data-testid="tk-config-save"
            className="px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save Changes
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* A. Identity */}
        <div data-testid="tk-config-identity">
          <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">A. Product Identity</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldText k="symbol" label="Token Symbol" hint="Changing the symbol after issuance requires migration" />
            <FieldText k="display_name" label="Display Name" />
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase font-semibold text-slate-600">Description</label>
              <textarea value={eff("description")} onChange={(e) => setDraft(d => ({...d, description: e.target.value}))}
                rows={3}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </div>
          </div>
        </div>

        {/* B. Yield */}
        <div data-testid="tk-config-yield">
          <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">B. Yield Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldText k="yield_apy_pct" label="Current APY" type="number" suffix="%" />
            <FieldSelect k="yield_calc_method" label="Calculation Method" options={["Daily Accrual", "Monthly Compound", "Quarterly"]} />
            <FieldSelect k="yield_distribution" label="Distribution Method" options={["In-Token", "Cash to USD Wallet"]} />
          </div>
        </div>

        {/* C. Issuance Limits */}
        <div data-testid="tk-config-limits">
          <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">C. Issuance Limits</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldText k="max_total_supply_usd" label="Maximum Total Supply" type="number" suffix="USD" hint={`Currently ${fmtUSDCompact(eff("max_total_supply_usd"))}`} />
            <FieldText k="max_per_member_usd" label="Maximum Per-Member Holding" type="number" suffix="USD" hint={`${brand.insurance} standard insurance limit is $250,000`} />
            <FieldText k="daily_mint_cap_usd" label="Daily Mint Cap" type="number" suffix="USD" />
            <FieldText k="daily_burn_cap_usd" label="Daily Burn Cap" type="number" suffix="USD" />
          </div>
        </div>

        {/* D. Compliance */}
        <div data-testid="tk-config-compliance">
          <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">D. Compliance Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldSelect k="required_kyc_tier" label="Required KYC Tier" options={["Basic", "Enhanced", "Institutional Only"]} />
            <FieldText k="reserve_ratio_floor" label="Reserve Ratio Floor" type="number" suffix="x"
              hint="GENIUS Act minimum is 1.00x — cannot go below" />
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">Auto-burn on closure</label>
              <button onClick={() => setDraft(d => ({...d, auto_burn_on_close: !eff("auto_burn_on_close")}))}
                data-testid="tk-config-autoburn"
                className={`mt-1 w-full px-3 py-2 rounded-md text-sm font-semibold ${eff("auto_burn_on_close") ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`}>
                {eff("auto_burn_on_close") ? "✓ Enabled" : "Disabled"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NewProductModal = ({ open, onClose, toast, onCreated }) => {
  const brand = useBranding();
  const [form, setForm] = useState({
    symbol: "", display_name: "", description: "",
    yield_apy_pct: 4.0, max_total_supply_usd: 10_000_000, max_per_member_usd: 250_000,
    required_kyc_tier: "Enhanced",
  });
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!form.symbol || !form.display_name) {
      toast("Symbol and display name required", "error"); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/products`, form);
      toast(`Issued new token: ${form.symbol}`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast(e.response?.data?.detail || "Failed", "error");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="tk-new-product-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Issue New Tokenized Product</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="text-[11px] uppercase font-semibold text-slate-600">Symbol</label>
            <input type="text" value={form.symbol} onChange={e => setForm({...form, symbol: e.target.value.toUpperCase()})}
              placeholder="TD-SEACOAST-PRO" className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono" data-testid="tk-newprod-symbol" />
          </div>
          <div>
            <label className="text-[11px] uppercase font-semibold text-slate-600">Display Name</label>
            <input type="text" value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})}
              placeholder={`${brand.name} Tokenized Deposit — Pro Tier`} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="tk-newprod-name" />
          </div>
          <div>
            <label className="text-[11px] uppercase font-semibold text-slate-600">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">APY %</label>
              <input type="number" value={form.yield_apy_pct} onChange={e => setForm({...form, yield_apy_pct: Number(e.target.value)})}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono" />
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-600">KYC Tier</label>
              <select value={form.required_kyc_tier} onChange={e => setForm({...form, required_kyc_tier: e.target.value})}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                <option>Basic</option><option>Enhanced</option><option>Institutional Only</option>
              </select>
            </div>
          </div>
          <button onClick={submit} disabled={saving} data-testid="tk-newprod-submit"
            className="w-full px-4 py-2.5 text-sm font-bold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 flex items-center justify-center gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Issue Token
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// SECTION 5 — Smart Contract Management
// ─────────────────────────────────────────────────

const ConfirmModal = ({ open, kind, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  // Emergency burn extra fields
  const [memberId, setMemberId] = useState("");
  const [amount, setAmount] = useState("");
  const [eventId, setEventId] = useState("");
  if (!open) return null;
  const isPause = kind === "pause";
  const submit = async () => {
    setSaving(true);
    try {
      await onConfirm(isPause
        ? { reason, admin_password: pwd }
        : { member_id: memberId, amount: Number(amount), compliance_event_id: eventId, reason, admin_password: pwd }
      );
      setReason(""); setPwd(""); setMemberId(""); setAmount(""); setEventId("");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid={`tk-confirm-${kind}`}>
      <div className={`bg-white rounded-2xl shadow-xl max-w-lg w-full border-t-8 ${isPause ? "border-amber-500" : "border-rose-600"}`} onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isPause ? <Pause className="w-5 h-5 text-amber-600" /> : <Flame className="w-5 h-5 text-rose-600" />}
            <h3 className="text-lg font-bold text-slate-900">
              {isPause ? "Confirm: Pause All Issuance" : "Confirm: Emergency Burn"}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <div className={`text-xs rounded-md p-3 ${isPause ? "bg-amber-50 text-amber-900" : "bg-rose-50 text-rose-900"}`}>
            <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> High-consequence action</p>
            <p className="mt-0.5">{isPause ? "All issuance bank-wide will halt. Member redemptions remain available. A critical event will be logged to compliance_events." : "Funds will be irreversibly destroyed. Holder will be flagged frozen. Requires Compliance Event ID."}</p>
          </div>
          {!isPause && (
            <>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-600">Member ID</label>
                <input type="text" value={memberId} onChange={e => setMemberId(e.target.value)} placeholder="TD-0001"
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono" data-testid="tk-emerg-member" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase font-semibold text-slate-600">Amount (USD)</label>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono" data-testid="tk-emerg-amount" />
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-slate-600">Compliance Event ID</label>
                  <input type="text" value={eventId} onChange={e => setEventId(e.target.value)} placeholder="CE-2026-XXXX"
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono" data-testid="tk-emerg-event" />
                </div>
              </div>
            </>
          )}
          <div>
            <label className="text-[11px] uppercase font-semibold text-slate-600">Reason</label>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)}
              placeholder={isPause ? "Operational — security advisory T-2026-08" : "Court-ordered seizure — case 2026-CV-1234"}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="tk-confirm-reason" />
          </div>
          <div>
            <label className="text-[11px] uppercase font-semibold text-slate-600 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Admin Password</label>
            <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="••••••••"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="tk-confirm-password" />
          </div>
          <p className="text-[11px] text-slate-500">Secondary admin approval simulation: <span className="font-semibold text-slate-700">approver@seacoast.com</span> (mock — auto-approved).</p>
          <button onClick={submit} disabled={saving || !reason || !pwd || (!isPause && (!memberId || !amount || !eventId))}
            data-testid={`tk-confirm-${kind}-submit`}
            className={`w-full px-4 py-2.5 text-sm font-bold rounded-md text-white disabled:opacity-40 flex items-center justify-center gap-1.5 ${isPause ? "bg-amber-600 hover:bg-amber-700" : "bg-rose-600 hover:bg-rose-700"}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isPause ? "PAUSE NOW" : "EXECUTE EMERGENCY BURN")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ContractsSection = ({ toast }) => {
  const [data, setData] = useState(null);
  const [confirmKind, setConfirmKind] = useState(null);
  const [confirmContract, setConfirmContract] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/contracts`);
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onConfirm = async (body) => {
    try {
      if (confirmKind === "pause") {
        await axios.post(`${API}/contracts/${confirmContract}/pause`, body);
        toast(`Contract ${confirmContract} paused — bank-wide issuance halted`);
      } else {
        await axios.post(`${API}/emergency-burn`, body);
        toast(`Emergency burn executed — logged to compliance_events`);
      }
      setConfirmKind(null);
      await load();
    } catch (e) {
      toast(e.response?.data?.detail || "Action failed", "error");
    }
  };

  if (!data) return <div className="bg-white rounded-xl border border-slate-200 p-5 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="tk-contracts-section">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900">Smart Contract Operations</h3>
        <p className="text-xs text-slate-500">Contract registry · emergency controls · upgrade path</p>
      </div>

      {/* 5A. Contract Registry */}
      <div className="mb-5">
        <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">A. Contract Registry</h4>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="tk-contracts-table">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5">Contract</th>
                <th className="text-left px-3 py-2.5">Chain</th>
                <th className="text-left px-3 py-2.5">Address</th>
                <th className="text-left px-3 py-2.5">Deploy</th>
                <th className="text-left px-3 py-2.5">Last Audit</th>
                <th className="text-left px-3 py-2.5">Audit Hash</th>
                <th className="text-left px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.contracts.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`tk-contract-row-${c.id}`}>
                  <td className="px-3 py-3 font-semibold text-slate-900">{c.name}</td>
                  <td className="px-3 py-3 text-slate-700">{c.chain}</td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-500">{truncAddr(c.address)}</td>
                  <td className="px-3 py-3 text-xs text-slate-500">{c.deploy_date}</td>
                  <td className="px-3 py-3 text-xs text-slate-500">{c.last_audit} <span className="text-slate-400">· {c.audit_firm}</span></td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-400">{c.audit_hash}</td>
                  <td className="px-3 py-3"><StatusPill status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5B. Emergency Controls */}
      <div className="mb-5 border-2 border-amber-200 bg-amber-50/40 rounded-xl p-4" data-testid="tk-emergency-section">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h4 className="text-sm font-bold text-amber-900 uppercase tracking-wide">Emergency Controls</h4>
        </div>
        <p className="text-xs text-amber-900/80 mb-3">High-consequence — require admin password + reason + compliance event reference. All actions write to compliance_events as CRITICAL.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button onClick={() => { setConfirmContract(data.contracts[0]?.id); setConfirmKind("pause"); }}
            data-testid="tk-pause-all-btn"
            className="px-4 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold flex items-center justify-center gap-2 shadow-sm">
            <Pause className="w-4 h-4" /> PAUSE ALL ISSUANCE
          </button>
          <button onClick={() => setConfirmKind("burn")}
            data-testid="tk-emergency-burn-btn"
            className="px-4 py-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold flex items-center justify-center gap-2 shadow-sm">
            <Flame className="w-4 h-4" /> EMERGENCY BURN
          </button>
        </div>
      </div>

      {/* 5C. Upgrade Path */}
      <div data-testid="tk-upgrades-section">
        <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">C. Scheduled Upgrades</h4>
        {data.upgrades.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">No scheduled upgrades.</p>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2.5">Upgrade</th>
                  <th className="text-left px-3 py-2.5">Target Chain</th>
                  <th className="text-left px-3 py-2.5">Schedule</th>
                  <th className="text-left px-3 py-2.5">Approver</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.upgrades.map((u) => (
                  <tr key={u.id} data-testid={`tk-upgrade-row-${u.id}`}>
                    <td className="px-3 py-3 font-semibold text-slate-900">{u.upgrade_name}</td>
                    <td className="px-3 py-3 text-slate-700">{u.target_chain}</td>
                    <td className="px-3 py-3 text-slate-700">{u.schedule}</td>
                    <td className="px-3 py-3 text-xs text-slate-600">{u.approver}</td>
                    <td className="px-3 py-3"><StatusPill status={u.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal open={!!confirmKind} kind={confirmKind} onClose={() => setConfirmKind(null)} onConfirm={onConfirm} />
    </div>
  );
};

// ─────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────

export const TokenizationConsoleTab = () => {
  const [overview, setOverview] = useState(null);
  const [holders, setHolders] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toastState, setToastState] = useState({ msg: "", kind: "success" });
  const toast = (msg, kind = "success") => setToastState({ msg, kind });

  const loadAll = useCallback(async () => {
    const [o, h] = await Promise.all([
      axios.get(`${API}/overview`),
      axios.get(`${API}/holders`),
    ]);
    setOverview(o.data);
    setHolders(h.data.holders);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll, refreshKey]);

  const onRefresh = async () => {
    setRefreshing(true);
    try { await loadAll(); setRefreshKey(k => k + 1); } finally { setRefreshing(false); }
  };

  return (
    <div data-testid="admin-tokenization-console">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Tokenization Console</h2>
          <p className="text-sm text-slate-500">
            Bank-controlled token issuance · holder ledger · authorized mint/burn · emergency controls
          </p>
        </div>
        <button onClick={onRefresh} disabled={refreshing} data-testid="tk-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="space-y-6">
        <OverviewSection data={overview} onIssueNew={() => setShowNewProduct(true)} />
        <HolderLedgerSection toast={toast} />
        <MintBurnSection holders={holders} toast={toast} onChanged={onRefresh} />
        <ProductConfigSection toast={toast} />
        <ContractsSection toast={toast} />
      </div>

      <NewProductModal open={showNewProduct} onClose={() => setShowNewProduct(false)} toast={toast} onCreated={onRefresh} />
      <Toast msg={toastState.msg} kind={toastState.kind} onClose={() => setToastState({ msg: "", kind: "success" })} />
    </div>
  );
};
